import React, { useState, useEffect, useCallback, useRef } from "react";
import { Box, CircularProgress, useMediaQuery } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import axios from "../../API/API";
import SearchHeader from "./SearchHeader";
import SearchFilter from "./SearchFilter/SearchFilter";
import { Tab, TabsNavigation } from "pepsico-ds";
import SearchItems from "./SearchItems";
import InternalCommonSection from "../InternalPages/CommonSection/InternalCommonSection";
import Loading from "../../utils/Loader";
import NoRecordFounds from "../../utils/NoRecordsFound";
import ViewSummary from "../ViewSummary/ViewSummary";
import { getAPI } from "../../API/ApiService";
import { BASE_URL } from "../../constant/apiUrl";
import { useQueryClient } from "@tanstack/react-query";
import {
  setSearchSuggestions,
  toggleRefreshSearchList,
} from "../../slices/searchResultSlice";
import { setFetchFavourtie } from "../../slices/internalPageSlice";
import { showErrorToast } from "../../utils/Toast/toast";

function buildSearchUrl(
  search,
  sector,
  variety,
  flesh,
  sort,
  year,
  selectedValue
) {
  const params = [];
  params.push(`phrase=${search || ""}`);
  params.push(`sector=${sector || ""}`);
  params.push(`variety=${variety || ""}`);
  params.push(`fleshColor=${flesh || ""}`);
  params.push(`sortBy=${sort || ""}`);
  params.push(`publicationRange=${year || "ALL"}`);
  params.push(`searchType=${selectedValue || "exactMatch"}`);
  return `${BASE_URL}/smart-search/initial?${params.join("&")}`;
}

export default function Search({ search: initialSearch }) {
  const dispatch = useDispatch();
  const selectedView = useSelector((state) => state.view);
  const { viewSummaryDrawer } = useSelector((store) => store.viewSummary);
  const { fetchFavourite } = useSelector((state) => state.internalPage);
  const isMobile = useMediaQuery("(max-width:600px)");
  const isDarktheme = useSelector((state) => state.theme);
  const queryClient = useQueryClient();
  const { oktauser } = useSelector((store) => store.user);

  // State
  const [grid, setGrid] = useState(selectedView);
  const [activeTab, setActiveTab] = useState("document"); // document, video, link, favourite
  const [variety, setVariety] = useState(null);
  const [sector, setSector] = useState(null);
  const [flesh, setFlesh] = useState(null);
  const [searchText, setSearchText] = useState(null);
  const [selectedValue, setSelectedValue] = useState("exactMatch");
  const [reload, setReload] = useState(false);
  const [loading, setLoading] = useState(false);
  const [favourite, setFavourite] = useState([]);
  const [sorted, setSorted] = useState("");
  const [year, setYear] = useState("");
  const [initialLoaded, setInitialLoaded] = useState(false);

  // Centralized infinite scroll state
  const [infiniteData, setInfiniteData] = useState({
    document: {
      items: [],
      sessionId: null,
      hasMore: false,
      page: 0,
      loadingMore: false,
      totalCount: 0,
    },
    video: {
      items: [],
      sessionId: null,
      hasMore: false,
      page: 0,
      loadingMore: false,
      totalCount: 0,
    },
    link: {
      items: [],
      sessionId: null,
      hasMore: false,
      page: 0,
      loadingMore: false,
      totalCount: 0,
    },
  });

  // Refs
  const sentinelRef = useRef(null);
  const observerRef = useRef(null);
  const isFetchingRef = useRef(false);

  // Create filter key for dependency tracking
  const filterKey = JSON.stringify({
    search: initialSearch || searchText,
    sector: sector?.label,
    variety: variety?.label,
    flesh: flesh?.label,
    sort: sorted?.value,
    year: year?.value,
    selectedValue,
  });

  // Reset fetchFavourite flag
  useEffect(() => {
    dispatch(setFetchFavourtie(false));
  }, [fetchFavourite, dispatch]);

  // Fetch function for initial search
  const fetchSearchResults = async () => {
    const url = buildSearchUrl(
      initialSearch,
      sector?.label,
      variety?.label,
      flesh?.label,
      sorted?.value,
      year?.value,
      selectedValue
    );
    const response = await getAPI(url);
    if (response?.status === 200) return response?.data;
    throw new Error("Failed to fetch");
  };

  // Main search query
  const { data, isLoading, refetch, isSuccess } = useQuery({
    queryKey: [
      "smartSearch",
      initialSearch,
      sector?.label,
      variety?.label,
      flesh?.label,
      sorted?.value,
      year?.value || "ALL",
      selectedValue,
      reload,
    ],
    queryFn: fetchSearchResults,
    refetchOnWindowFocus: false,
    staleTime: 0,
  });

  useEffect(() => {
    if (!isSuccess || !data) return;

    // 🔥 Call second API here

    const fetChSuggetions = async () => {
      try {
        const userId = oktauser?.useruniquename || "";
        const response = await getAPI(
          `${BASE_URL}/keyword-search/all?userId=${userId}`
        );
        if (response?.status === 200) {
          dispatch(setSearchSuggestions(response?.data));
        }
      } catch (e) {
        showErrorToast("Failed to Load Suggestions");
      }
    };

    if (oktauser && Object.keys(oktauser).length > 0) {
      fetChSuggetions();
    }
  }, [isSuccess, data]);

  // Fetch favourites separately
  const fetchFavourites = useCallback(async () => {
    const url = buildSearchUrl(
      initialSearch,
      sector?.label,
      variety?.label,
      flesh?.label,
      sorted?.value,
      year?.value,
      selectedValue
    );
    const response = await getAPI(url);
    return response?.data?.favourites || [];
  }, [initialSearch, sector, variety, flesh, selectedValue, sorted, year]);

  // Initialize infinite data from main query result
  useEffect(() => {
    if (!data) return;

    setInfiniteData((prev) => ({
      document: {
        ...prev.document,
        items: data?.documents || [],
        sessionId: data?.documentSessionId || null,
        hasMore: Boolean(data?.isNextPageAvailableDocuments),
        totalCount: data?.totalDocuments || 0,
      },
      video: {
        ...prev.video,
        items: data?.videos || [],
        sessionId: data?.videoSessionId || null,
        hasMore: Boolean(data?.isNextPageAvailableVideos),
        totalCount: data?.totalVideos || 0,
      },
      link: {
        ...prev.link,
        items: data?.links || [],
        sessionId: data?.linkSessionId || null,
        hasMore: Boolean(data?.isNextPageAvailableLinks),
        totalCount: data?.totalLinks || 0,
      },
    }));

    // Update favourites
    if (data?.favourites) {
      setFavourite(data.favourites);
    }
  }, [data]);
  useEffect(() => {
    if (!isLoading && data) {
      setInitialLoaded(true);
    }
  }, [isLoading, data]);
  // Update favourites when fetchFavourite is triggered
  useEffect(() => {
    const updateFavourites = async () => {
      if (fetchFavourite) {
        const freshFavourites = await fetchFavourites();
        setFavourite(freshFavourites);
      }
    };
    updateFavourites();
  }, [fetchFavourite, fetchFavourites]);

  // Handle fetchFavourite trigger
  useEffect(() => {
    if (fetchFavourite) {
      queryClient.invalidateQueries(["smartSearch"]);
      refetch().then(() => {
        dispatch(setFetchFavourtie(false));
      });
    }
  }, [fetchFavourite, queryClient, refetch, dispatch]);

  // Build query parameters for scroll API
  const buildScrollParams = useCallback(
    (contentType) => {
      const sessionId = infiniteData[contentType]?.sessionId;
      if (!sessionId) return null;

      return {
        phrase: initialSearch || searchText || "",
        sector: sector?.label || "",
        variety: variety?.label || "",
        fleshColor: flesh?.label || "",
        sortBy: sorted?.value || "",
        publicationRange: year?.value || "ALL",
        searchType: selectedValue || "",
        contentType: contentType || "",
        searchSessionId: sessionId,
      };
    },
    [
      initialSearch,
      searchText,
      sector,
      variety,
      flesh,
      sorted,
      year,
      selectedValue,
      infiniteData,
    ]
  );

  // Centralized function to load more items
  const loadMoreItems = useCallback(async () => {
    if (isFetchingRef.current) return;

    const currentTabData = infiniteData[activeTab];
    if (!currentTabData?.hasMore || !currentTabData?.sessionId) return;

    isFetchingRef.current = true;

    // Set loading state for current tab
    setInfiniteData((prev) => ({
      ...prev,
      [activeTab]: {
        ...prev[activeTab],
        loadingMore: true,
      },
    }));

    try {
      const params = buildScrollParams(activeTab);
      if (!params) return;

      const response = await axios.get(`${BASE_URL}/smart-search/scroll`, {
        params,
      });

      if (response?.status === 200) {
        const newContent = response?.data?.content || [];

        // Merge without duplicates
        setInfiniteData((prev) => {
          const currentItems = prev[activeTab].items;
          const ids = new Set(currentItems.map((item) => String(item.id)));
          const uniqueNewItems = newContent.filter(
            (item) => !ids.has(String(item.id))
          );

          return {
            ...prev,
            [activeTab]: {
              ...prev[activeTab],
              items: [...currentItems, ...uniqueNewItems],
              hasMore: response?.data?.isNextPageAvailable || false,
              loadingMore: false,
              page: prev[activeTab].page + 1,
            },
          };
        });

        console.log(`Loaded ${newContent?.length} more items for ${activeTab}`);
      }
    } catch (err) {
      console.error(`Scroll error for ${activeTab}:`, err);
      setInfiniteData((prev) => ({
        ...prev,
        [activeTab]: {
          ...prev[activeTab],
          hasMore: false,
          loadingMore: false,
        },
      }));
    } finally {
      isFetchingRef.current = false;
    }
  }, [activeTab, infiniteData, buildScrollParams]);

  // Setup Intersection Observer for infinite scroll
  useEffect(() => {
    const currentTabData = infiniteData[activeTab];

    // Don't setup observer if:
    // 1. No more items to load
    // 2. Currently loading
    // 3. No items yet
    if (
      !currentTabData?.hasMore ||
      currentTabData?.loadingMore ||
      currentTabData?.items?.length === 0
    ) {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      return;
    }

    // Clean up previous observer
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    // Create new observer
    observerRef.current = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && !isFetchingRef.current) {
          console.log(`Sentinel visible for ${activeTab}, loading more...`);
          loadMoreItems();
        }
      },
      { root: null, rootMargin: "300px", threshold: 0.1 }
    );

    // Observe the sentinel
    if (sentinelRef.current) {
      observerRef.current.observe(sentinelRef.current);
    }

    // Cleanup function
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [
    activeTab,
    infiniteData[activeTab]?.hasMore,
    infiniteData[activeTab]?.loadingMore,
    infiniteData[activeTab]?.items.length,
    loadMoreItems,
  ]);

  // Reset infinite scroll data when filters change

  // Handle tab change
  const handleTabChange = (index, tabType) => {
    setActiveTab(tabType);

    // Dispatch to refresh search list if needed
    dispatch(toggleRefreshSearchList());
  };

  if (isLoading) return <Loading />;

  const font = isMobile ? "12px" : " ";

  return (
    <Box
      sx={{
        paddingTop: "16px",
        display: "flex",
        flexDirection: "column",
        gap: "24px",
        paddingX:
          grid === "tile"
            ? { xs: "0px", md: "53px" }
            : { xs: "0px", md: "25px" },
        backgroundColor:
          grid !== "tile" && !isDarktheme
            ? { xs: "none", md: "white" }
            : "none",
        paddingBottom: "24px",
      }}
    >
      <Box
        sx={{
          padding: grid !== "tile" ? { xs: "0px", md: "32px" } : undefined,
          display: "flex",
          flexDirection: "column",
          gap: "24px",
          boxShadow:
            grid !== "tile"
              ? `var(--Elevation-X-Elevation-03, 0px) var(--Elevation-Y-Elevation-03, 2px) var(--Elevation-Blur-Elevation-03, 24px) var(--Elevation-Spread-Elevation-03, 8px) var(--Elevation-Colors-Elevation-03, rgba(0, 92, 188, 0.12))`
              : undefined,
        }}
      >
        <Box
          sx={{
            padding: "16px",
            display: "flex",
            flexDirection: "column",
            gap: "24px",
          }}
        >
          <SearchHeader search={initialSearch} />

          <SearchFilter
            grid={grid}
            setGrid={setGrid}
            setSearch={setSearchText}
            setVariety={setVariety}
            searchText={searchText}
            setSector={setSector}
            setFlesh={setFlesh}
            setSelectedValue={setSelectedValue}
            selectedValue={selectedValue}
            refetch={refetch}
            setLoad={() => {}}
            setReload={setReload}
            variety={variety}
            sector={sector}
            flesh={flesh}
            setSorted={setSorted}
            sort={sorted}
            year={year}
            setYear={setYear}
          />

          <TabsNavigation
            selectedIndex={["document", "video", "link", "favourite"].indexOf(
              activeTab
            )}
          >
            <Tab
              label={`Documents (${infiniteData.document.totalCount || 0})`}
              onClick={() => handleTabChange(0, "document")}
              style={{ fontSize: font }}
            />
            <Tab
              label={`Videos (${infiniteData.video.totalCount || 0})`}
              onClick={() => handleTabChange(1, "video")}
              style={{ fontSize: font }}
            />
            <Tab
              label={`Links (${infiniteData.link.totalCount || 0})`}
              onClick={() => handleTabChange(2, "link")}
              style={{ fontSize: font }}
            />
            <Tab
              label={`Favorites (${data?.totalFavourites || 0})`}
              onClick={() => handleTabChange(3, "favourite")}
              style={{ fontSize: font }}
            />
          </TabsNavigation>
        </Box>

        {/* Documents Tab */}
        {activeTab === "document" && (
          <SearchItems
            grid={grid}
            items={infiniteData?.document?.items}
            content="Documents"
            isLoadingMore={infiniteData?.document?.loadingMore}
            setLoading={setLoading}
            hasItems={infiniteData?.document?.items.length > 0}
            totalCount={infiniteData?.document?.totalCount}
            initialLoaded={initialLoaded}
          />
        )}

        {/* Videos Tab */}
        {activeTab === "video" && (
          <SearchItems
            grid={grid}
            items={infiniteData?.video?.items}
            content="Videos"
            isLoadingMore={isLoading}
            setLoading={setLoading}
            hasItems={infiniteData?.video?.items?.length > 0}
            totalCount={infiniteData?.video?.totalCount}
            initialLoaded={initialLoaded}
          />
        )}

        {/* Links Tab */}
        {activeTab === "link" && (
          <SearchItems
            grid={grid}
            items={infiniteData.link.items}
            content="Links"
            isLoadingMore={isLoading}
            setLoading={setLoading}
            hasItems={infiniteData.link.items.length > 0}
            totalCount={infiniteData.link.totalCount}
            initialLoaded={initialLoaded}
          />
        )}

        {/* Favourites Tab */}
        {activeTab === "favourite" &&
          (favourite.length === 0 ? (
            <NoRecordFounds />
          ) : (
            <InternalCommonSection data={favourite} grid={grid} chapter />
          ))}

        {/* Sentinel for infinite scroll - only for content tabs */}
        {activeTab !== "favourite" && infiniteData[activeTab]?.hasMore && (
          <div
            ref={sentinelRef}
            style={{
              height: "20px",
              width: "100%",
              marginTop: "20px",
              marginBottom: "20px",
              backgroundColor: "transparent",
            }}
          />
        )}

        {/* Loading indicator for infinite scroll */}
        {activeTab !== "favourite" && infiniteData[activeTab]?.loadingMore && (
          <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
            <CircularProgress />
          </Box>
        )}
      </Box>

      {viewSummaryDrawer && <ViewSummary />}
      {loading && <Loading />}
    </Box>
  );
}
